import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Table,
  Button,
  Modal,
  Form,
} from "react-bootstrap";
import { useSearchParams, Link } from "react-router-dom";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";

const DriverApprovalDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get("driverId") || "";

  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [approving, setApproving] = useState<boolean>(false);
  const [docMap, setDocMap] = useState<Record<string, any>>({});
  const objectUrlsRef = useRef<string[]>([]);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalSrc, setModalSrc] = useState<string | null>(null);
  const [modalTitle, setModalTitle] = useState<string>("");

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [formData, setFormData] = useState<any>({});
  const [saving, setSaving] = useState<boolean>(false);

  // ---------------------------------------------------
  // FETCH DRIVER DETAILS
  // ---------------------------------------------------
  const fetchDriverDetails = async () => {
    if (!customerId) return;
    setLoading(true);

    try {
      const token = localStorage.getItem("authToken");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const url = `${API_URL.GET_DRIVER_DETAILS}/${customerId}`;
      const response = await axiosInstance.get(url, { headers });

      setDetails(response.data);
      // Initialize form data
      setFormData({
        firstName: response.data.firstName || "",
        lastName: response.data.lastName || "",
        licence: response.data.licence || "",
        emiratesId: response.data.emiratesId || "",
        customerDocs: response.data.customerDocs
          ? JSON.parse(JSON.stringify(response.data.customerDocs))
          : {},
        // Keep other fields for payload if needed
        gender: response.data.gender,
        serviceType: response.data.serviceType || response.data.service_type,
        lastLevel: response.data.lastLevel || response.data.last_level,
      });
    } catch (error: any) {
      console.error(
        "GET_DRIVER_DETAILS error:",
        error?.response?.data || error
      );
      setDetails(null);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------
  // FETCH INDIVIDUAL DOCUMENTS (image)
  // ---------------------------------------------------
  const fetchDocumentById = async (docId: string) => {
    if (!docId) return null;

    try {
      const token = localStorage.getItem("authToken");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const url = `${API_URL.GET_DOCUMENTS}/${docId}`;

      // Try fetching as blob (image)
      const res = await axiosInstance.get(url, {
        headers,
        responseType: "blob",
      });

      const blob = res.data;
      const objectUrl = URL.createObjectURL(blob);
      objectUrlsRef.current.push(objectUrl);

      return { src: objectUrl, raw: blob };
    } catch (err: any) {
      console.error(
        "Error fetching document",
        err?.response?.data || err.message
      );

      // fallback → try without blob
      try {
        const token = localStorage.getItem("authToken");
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const url = `${API_URL.GET_DOCUMENTS}/${docId}`;
        const res2 = await axiosInstance.get(url, { headers });

        const data = res2.data;
        if (data?.url || data?.fileUrl || data?.data) {
          return { src: data.url || data.fileUrl || data.data };
        }
      } catch {}

      return null;
    }
  };

  // ---------------------------------------------------
  // LOAD DOCUMENTS AFTER DRIVER DETAILS
  // ---------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const loadDocs = async () => {
      if (!details?.customerDocs) return;

      const entries = Object.entries(details.customerDocs) as [string, any][];
      const map: Record<string, any> = {};

      for (const [key, doc] of entries) {
        const docId = doc?.docId;
        const docName = doc?.docName || key;

        if (!docId) {
          map[key] = { docName, src: null };
          continue;
        }

        const fetched = await fetchDocumentById(docId);
        if (cancelled) break;

        map[docId] = {
          docName,
          src: fetched?.src || null,
          raw: fetched?.raw || null,
          docType: doc.docType || key,
        };
      }

      if (!cancelled) setDocMap(map);
    };

    loadDocs();

    return () => {
      cancelled = true;
    };
  }, [details]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      objectUrlsRef.current = [];
    };
  }, []);

  useEffect(() => {
    fetchDriverDetails();
  }, [customerId]);

  // ---------------------------------------------------
  // APPROVE DRIVER
  // ---------------------------------------------------
  const handleApprove = async () => {
    if (!details || !customerId) return;

    setApproving(true);

    try {
      const token = localStorage.getItem("authToken");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const payload = {
        status: "APPROVED",
        customer_id: customerId,
        acknowledgement: "approve",
      };

      const resp = await axiosInstance.post(API_URL.APPROVE_DRIVER, payload, {
        headers,
      });

      if (resp?.status === 200 || resp?.status === 201) {
        setDetails((prev: any) => ({
          ...(prev || {}),
          approvalStatus: "APPROVED",
        }));
      }
    } catch (err: any) {
      console.error(
        "APPROVE_DRIVER error:",
        err?.response?.data || err.message
      );
    } finally {
      setApproving(false);
    }
  };

  // ---------------------------------------------------
  // EDIT HANDLERS
  // ---------------------------------------------------
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    // Reset form data on cancel
    if (isEditing && details) {
      setFormData({
        firstName: details.firstName || "",
        lastName: details.lastName || "",
        licence: details.licence || "",
        emiratesId: details.emiratesId || "",
        customerDocs: details.customerDocs
          ? JSON.parse(JSON.stringify(details.customerDocs))
          : {},
        gender: details.gender,
        serviceType: details.serviceType || details.service_type,
        lastLevel: details.lastLevel || details.last_level,
      });
    }
  };

  const handleTextChange = (field: string, val: string) => {
    setFormData((prev: any) => ({ ...prev, [field]: val }));
  };

  const handleDocNameChange = (key: string, val: string) => {
    setFormData((prev: any) => ({
      ...prev,
      customerDocs: {
        ...prev.customerDocs,
        [key]: {
          ...prev.customerDocs[key],
          docName: val,
        },
      },
    }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Create a local blob for immediate preview (optional but good UX)
    const localUrl = URL.createObjectURL(file);
    objectUrlsRef.current.push(localUrl);

    try {
      const token = localStorage.getItem("authToken"); // Or access_token
      const headers: any = {
          "Content-Type": "multipart/form-data"
      };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const uploadData = new FormData();
      uploadData.append("document", file);

      // Upload to get UUID
      const response = await axiosInstance.post(API_URL.DOC_UPLOAD, uploadData, { headers });

      if (response.data && response.data.uuid) {
          const newUuid = response.data.uuid;

          // Update formData so it has the new docId for when we save
          setFormData((prev: any) => ({
            ...prev,
            customerDocs: {
              ...prev.customerDocs,
              [key]: {
                ...prev.customerDocs[key],
                docId: newUuid,
              }
            }
          }));

          // Also update docMap so the image is visible now
          // We map docId -> { src, docName, docType }
          setDocMap((prev) => ({
             ...prev,
             [newUuid]: {
                 docName: formData.customerDocs?.[key]?.docName,
                 src: localUrl,
                 docType: key,
                 raw: file
             }
          }));
      }

    } catch (err: any) {
        console.error("File upload error:", err);
    }
  };

  const handleSave = async () => {
    if (!customerId) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      // Transform docs object to array for payload
      const docsArray = Object.entries(formData.customerDocs || {}).map(
        ([key, val]: any) => ({
          docName: val.docName,
          docType: val.docType || key, // docType is usually the key or inside the object
          docId: val.docId || null,
        })
      );

      const payload = {
        customer_id: customerId,
        firstname: formData.firstName,
        lastname: formData.lastName,
        gender: formData.gender || "Male",
        service_type: formData.serviceType || "RSA",
        emirates_id: formData.emiratesId,
        licence_number: formData.licence,
        customerDocs: docsArray,
        last_level: formData.lastLevel || "999",
      };

      const response = await axiosInstance.put(API_URL.DOCUMENTS_EDIT, payload, {
        headers,
      });

      if (response.status === 200 || response.status === 201) {
        await fetchDriverDetails();
        setIsEditing(false);
      }
    } catch (err: any) {
      console.error(
        "SAVE_DRIVER_DETAILS error:",
        err?.response?.data || err.message
      );
      // Could show toast here
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------
  // RENDER ADDRESS
  // ---------------------------------------------------
  const renderAddress = (addr: any) => {
    if (!addr) return <div>-</div>;

    return (
      <div>
        {addr.address_1 && <div>{addr.address_1}</div>}
        {addr.street && <div>{addr.street}</div>}
        {addr.city && <div>{addr.city}</div>}
        {addr.state && <div>{addr.state}</div>}
        {addr.country && <div>{addr.country}</div>}
        {addr.pin_code && <div>PIN: {addr.pin_code}</div>}
      </div>
    );
  };

  // ---------------------------------------------------
  // REVISED DOCUMENTS UI
  // ---------------------------------------------------
  // ---------------------------------------------------
  // REVISED DOCUMENTS UI
  // ---------------------------------------------------
  const renderDocs = (docs: any) => {
    // If editing, we use formData.customerDocs, else details.customerDocs
    const sourceDocs = isEditing ? formData.customerDocs : docs;

    if (!sourceDocs || Object.keys(sourceDocs).length === 0)
      return <div className="text-muted">No documents uploaded</div>;

    const entries = Object.entries(sourceDocs) as [string, any][];

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: "20px",
          marginTop: "10px",
        }}
      >
        {entries.map(([key, val]: any) => {
          const docId = val.docId;
          const mapped = docMap[docId];
          const displayType = (val.docType || key || "").replace(/_/g, " ");

          return (
            <div
              key={key}
              style={{
                background: "#fff",
                borderRadius: "10px",
                padding: "10px",
                border: "1px solid #e8e8e8",
                textAlign: "center",
                boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                transition: "0.25s",
              }}
              className="doc-tile"
            >
               <div style={{ fontWeight: 700, fontSize: "12px", color: "#555", marginBottom: "8px", textTransform: "uppercase" }}>
                  {displayType}
              </div>

              {mapped?.src ? (
                <div
                  style={{
                    height: "140px",
                    overflow: "hidden",
                    borderRadius: "8px",
                    marginBottom: "10px",
                    cursor: "pointer",
                    border: "1px solid #ddd",
                  }}
                  onClick={() => {
                    setModalSrc(mapped.src);
                    setModalTitle(displayType);
                    setShowModal(true);
                  }}
                >
                  <img
                    src={mapped.src}
                    alt={displayType}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "0.3s",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.transform = "scale(1.05)")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                  />
                </div>
              ) : (
                <div
                  style={{
                    height: "140px",
                    border: "1px dashed #aaa",
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "10px",
                    color: "#888",
                  }}
                >
                  Not Available
                </div>
              )}

              <div style={{ fontWeight: 600, fontSize: "14px" }}>
                {isEditing ? (
                  <>
                    <Form.Control
                        type="text"
                        size="sm"
                        value={val.docName || ""}
                        placeholder="Doc Name/Number"
                        onChange={(e) => handleDocNameChange(key, e.target.value)}
                        className="mt-2"
                    />
                    <div className="mt-2 text-start">
                        <Form.Label style={{fontSize: "12px", marginBottom: "2px"}}>Replace File</Form.Label>
                        <Form.Control 
                            type="file" 
                            size="sm" 
                            accept="image/*,application/pdf"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileUpload(e, key)}
                        />
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ---------------------------------------------------
  // MAIN UI RENDER
  // ---------------------------------------------------
  return (
    <div className="row">
      <Card className="card-bg-afl text-start">
        <CardBody className="px-3 pt-2">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="car_upload_text">Driver Approval Details</h4>

            <div className="d-flex gap-2">
              {!isEditing ? (
                <Button
                  className="btn all_button_style text-black"
                  onClick={handleEditToggle}
                  disabled={saving}
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    className="btn btn-success text-white"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    className="btn btn-secondary text-white"
                    onClick={handleEditToggle}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </>
              )}

              {!isEditing && details?.approvalStatus === "PENDING" && (
                <Button
                  className="btn all_button_style text-black"
                  onClick={handleApprove}
                  disabled={approving}
                >
                  {approving ? "Approving..." : "Approve Driver"}
                </Button>
              )}
              <Link to="/vendor/dashboard/driverapproval">
                <Button className="btn all_button_style text-black">
                  Back
                </Button>
              </Link>
            </div>
          </div>

          {/* LOADING / ERRORS */}
          {loading && <div className="mt-3">Loading...</div>}
          {!loading && !details && (
            <div className="mt-3 text-danger">No driver details found</div>
          )}

          {/* ---------------- DRIVER DETAILS ---------------- */}
          {details && (
            <>
              <div className="mt-3">
              <Row className="g-3">
                  <Col md={4}>
                    <div className="car_upload_text">First Name</div>
                    {isEditing ? (
                        <Form.Control 
                            type="text" 
                            value={formData.firstName} 
                            onChange={(e) => handleTextChange("firstName", e.target.value)}
                        />
                    ) : (
                        <div className="list_status_grey_text">
                        {details.firstName} {details.lastName}
                        </div>
                    )}
                  </Col>
                  <Col md={4}>
                    <div className="car_upload_text">Last Name</div>
                    {isEditing ? (
                        <Form.Control 
                            type="text" 
                            value={formData.lastName} 
                            onChange={(e) => handleTextChange("lastName", e.target.value)}
                        />
                    ) : (
                        <div className="list_status_grey_text">
                           {details.lastName}
                        </div>
                    )}
                  </Col>

                  <Col md={4}>
                    <div className="car_upload_text">Status</div>
                    <span
                      className="badge rounded-pill"
                      style={{
                        backgroundColor:
                          details.approvalStatus === "APPROVED"
                            ? "#2DC24E"
                            : "#ffc107",
                      }}
                    >
                      {details.approvalStatus}
                    </span>
                  </Col>

                  <Col md={4}>
                    <div className="car_upload_text">Phone</div>
                    <div className="list_status_grey_text">
                      {details.phoneNumber || details.phone || "-"}
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="car_upload_text">Licence</div>
                    {isEditing ? (
                        <Form.Control 
                            type="text" 
                            value={formData.licence} 
                            onChange={(e) => handleTextChange("licence", e.target.value)}
                        />
                    ) : (
                        <div className="list_status_grey_text">
                        {details.licence || "-"}
                        </div>
                    )}
                  </Col>
                  <Col md={4}>
                    <div className="car_upload_text">Emirates ID</div>
                    {isEditing ? (
                        <Form.Control 
                            type="text" 
                            value={formData.emiratesId} 
                            onChange={(e) => handleTextChange("emiratesId", e.target.value)}
                        />
                    ) : (
                        <div className="list_status_grey_text">
                        {details.emiratesId || "-"}
                        </div>
                    )}
                  </Col>
                </Row>
              </div>

              {/* ---------------- ADDRESS + DOCUMENTS ---------------- */}
              <div className="mt-4">
                <Row>
                  <Col>
                    <h5 style={{ fontWeight: 600 }}>Documents</h5>
                    <div
                      style={{
                        borderBottom: "2px solid #eee",
                        marginBottom: "15px",
                      }}
                    ></div>
                    {renderDocs(details.customerDocs)}
                  </Col>
                </Row>
              </div>
            </>
          )}

          {/* ---------------- IMAGE MODAL ---------------- */}
          <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            centered
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ textAlign: "center" }}>
              {modalSrc ? (
                <img
                  src={modalSrc}
                  alt={modalTitle}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "80vh",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <div>No image</div>
              )}
            </Modal.Body>
          </Modal>
        </CardBody>
      </Card>
    </div>
  );
};

export default DriverApprovalDetails;
